import * as Yup from 'yup';
import { canCurrentUser } from '@utils/Permissions';
import { useVisibleBranchOffices } from '@hooks/usePermissionSubject';
import { Capability } from '@utils/Permissions/Capabilities';
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import React, { useEffect, useMemo } from 'react';
import { OpenModalHookType } from '@hooks/useOpenModal';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { useSelector } from 'react-redux';
import { RootReducer } from '@store/index';
import { DiscountType, PromotionalCode } from '@api/PromotionalCodes/PromotionalCode';

interface ModalSavePromotionalCodeProps {
    modalSavePromotionalCode: OpenModalHookType;
    promotionalCode?: PromotionalCode;
    onSavePromotionalCode: (promotionalCode: PromotionalCode) => void;
}

const savePromotionalCodeSchema = Yup.object({
    id: Yup.string(),
    code: Yup.string().required('Codigo requerido'),
    noLimit: Yup.boolean(),
    usageLimit: Yup.number().required('Limite de uso requerido'),
    branchOfficesAvailable: Yup.array().min(1, 'Debes de elegir al menos una sucursal').required('Sucursal requerida'),
    discountType: Yup.string().required('Tipo de descuento requerido'),
    discountValue: Yup.number().required('Valor de descuento requerido')
});

export const ModalSavePromotionalCode: React.FunctionComponent<ModalSavePromotionalCodeProps> = (props: any) => {
    const { modalSavePromotionalCode, promotionalCode, onSavePromotionalCode } = props;
    // Las sucursales que ve quien opera (en "Ver como", la del sidebar) más la opción "Todas".
    const branchOfficesCatalog = useVisibleBranchOffices();
    const { branchOfficeSelected } = useSelector((state: RootReducer) => state.auth);
    // "Todas las sucursales" es del admin general (F6): el admin de sucursal limita
    // el código a las suyas, y por eso arranca en la sucursal en operación.
    const canGlobal = canCurrentUser(Capability.PromoCodesManageGlobal);
    const branchOffices = useMemo(
        () => (canGlobal ? [...branchOfficesCatalog, { id: 'all', name: 'Todas las sucursales' }] : branchOfficesCatalog),
        [branchOfficesCatalog, canGlobal]
    );
    const defaultBranches = canGlobal ? ['all'] : branchOfficeSelected?.id ? [branchOfficeSelected.id] : [];

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors }
    } = useForm<PromotionalCode>({
        resolver: yupResolver(savePromotionalCodeSchema)
    });
    const handleClose = () => {
        modalSavePromotionalCode.toggle();
    };

    const onSavePromotionalCodeForm: SubmitHandler<PromotionalCode> = async (values) => {
        onSavePromotionalCode(values);
    };

    useEffect(() => {
        if (modalSavePromotionalCode.modalOpen) {
            reset({
                id: promotionalCode?.id || undefined,
                code: promotionalCode?.code || '',
                noLimit: promotionalCode?.noLimit,
                usageLimit: promotionalCode?.usageLimit || 0,
                branchOfficesAvailable: promotionalCode?.branchOfficesAvailable || defaultBranches,
                discountType: promotionalCode?.discountType || DiscountType.Amount,
                discountValue: promotionalCode?.discountValue || 0
            });
        }
    }, [modalSavePromotionalCode.modalOpen]);

    return (
        <Dialog
            PaperProps={{
                style: { borderRadius: 15, padding: 4 }
            }}
            open={modalSavePromotionalCode.modalOpen}
            onClose={handleClose}
            aria-labelledby="modal-save-promotional-code-title"
            aria-describedby="modal-save-promotional-code-description"
            fullWidth
            maxWidth="sm"
        >
            <DialogTitle id="modal-save-promotional-code-title">
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" fontWeight={600}>
                        Nuevo código promocional
                    </Typography>
                    <IconButton color="secondary" onClick={handleClose}>
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent>
                <form id="add-promotional-code-form" onSubmit={handleSubmit(onSavePromotionalCodeForm)}>
                    <Grid container spacing={2} mt={2}>
                        <Grid item xs={12}>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <Controller
                                        name="code"
                                        control={control}
                                        render={({ field: { onChange, value } }) => (
                                            <TextField
                                                fullWidth
                                                required
                                                label="Nombre del código"
                                                variant="outlined"
                                                value={value}
                                                onChange={onChange}
                                                error={!!errors.code}
                                                helperText={errors.code && errors.code.message}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Controller
                                        name="noLimit"
                                        control={control}
                                        render={({ field: { onChange, value } }) => (
                                            <Stack direction="row" alignItems="center">
                                                <Checkbox checked={value} onChange={onChange} />
                                                <Typography variant="body1">Código sin limite</Typography>
                                            </Stack>
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Controller
                                        name="usageLimit"
                                        control={control}
                                        render={({ field: { onChange, value } }) => (
                                            <TextField
                                                fullWidth
                                                required
                                                type="number"
                                                label="Limite de usos"
                                                variant="outlined"
                                                value={value}
                                                onChange={onChange}
                                                error={!!errors.usageLimit}
                                                helperText={errors.usageLimit?.message}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <FormControl fullWidth>
                                        <InputLabel id="branch-offices-label">Disponible en</InputLabel>
                                        <Controller
                                            name="branchOfficesAvailable"
                                            control={control}
                                            render={({ field: { onChange, value } }) => (
                                                <Select
                                                    labelId="branch-offices-label"
                                                    id="branch-offices"
                                                    multiple
                                                    value={value}
                                                    label="Sucursal(es)"
                                                    onChange={onChange}
                                                    error={!!errors.branchOfficesAvailable}
                                                >
                                                    {branchOffices.map((branchOffice, index) => (
                                                        <MenuItem key={index} value={branchOffice.id}>
                                                            {branchOffice.name}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            )}
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <FormControl fullWidth>
                                        <InputLabel id="disscount-types-label">Tipo de descuento</InputLabel>
                                        <Controller
                                            name="discountType"
                                            control={control}
                                            render={({ field: { onChange, value } }) => (
                                                <Select
                                                    labelId="disscount-types-label"
                                                    id="disscount-types"
                                                    value={value}
                                                    label="Tipo de descuento"
                                                    onChange={onChange}
                                                    error={!!errors.discountType}
                                                >
                                                    <MenuItem value={DiscountType.Amount}>
                                                        {DiscountType.Amount}
                                                    </MenuItem>
                                                    <MenuItem value={DiscountType.Percent}>
                                                        {DiscountType.Percent}
                                                    </MenuItem>
                                                </Select>
                                            )}
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Controller
                                        name="discountValue"
                                        control={control}
                                        render={({ field: { onChange, value } }) => (
                                            <TextField
                                                fullWidth
                                                required
                                                type="number"
                                                label="Monto de descuento"
                                                variant="outlined"
                                                value={value}
                                                onChange={onChange}
                                                error={!!errors.discountValue}
                                                helperText={errors.discountValue?.message}
                                            />
                                        )}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </form>
            </DialogContent>
            <DialogActions>
                <Box flex={1} textAlign="center">
                    <Button
                        sx={{ width: 300 }}
                        color="primary"
                        variant="contained"
                        type="submit"
                        form="add-promotional-code-form"
                    >
                        Guardar
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};
